import { createParser } from 'eventsource-parser';

const PROXY_BASE =
  (import.meta.env.VITE_CDC_PROXY_URL as string | undefined) ?? 'http://localhost:8787';

/** Anthropic prompt-cache breakpoint marker. Set on the last block of any prefix
 *  that should be cached for ~5 minutes. */
export type CacheControl = { type: 'ephemeral' };

export type ApiContent =
  | { type: 'text'; text: string; cache_control?: CacheControl }
  | {
      type: 'image';
      source: {
        type: 'base64';
        media_type: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
        data: string;
      };
    }
  | {
      // Anthropic native PDF support — see anthropic.com/news/pdf-support.
      // The proxy already passes everything through, no extra header required.
      type: 'document';
      source: {
        type: 'base64';
        media_type: 'application/pdf';
        data: string;
      };
    };

export interface ApiMessage {
  role: 'user' | 'assistant';
  content: string | ApiContent[];
}

export type SystemBlock = { type: 'text'; text: string; cache_control?: CacheControl };

export interface StreamMessageOpts {
  apiKey: string;
  model: string;
  /** Plain string is auto-wrapped into a single cached system block. Pass an array
   *  to control cache breakpoints across multiple system blocks. */
  system: string | SystemBlock[];
  messages: ApiMessage[];
  signal?: AbortSignal;
  maxTokens?: number;
  /** Called for each raw chunk of upstream bytes (for progress UI / watchdog). */
  onUpstreamBytes?: (bytes: number) => void;
}

export class StreamError extends Error {
  status: number;
  bodyText: string;
  constructor(message: string, status: number, bodyText: string) {
    super(message);
    this.name = 'StreamError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

/** Raised when the local proxy is unreachable (most often: dev server isn't running). */
export class ProxyUnreachableError extends Error {
  constructor(public proxyUrl: string, cause?: unknown) {
    super(`Cannot reach the local proxy at ${proxyUrl}. Is the dev server running?`);
    this.name = 'ProxyUnreachableError';
    if (cause) (this as Error & { cause?: unknown }).cause = cause;
  }
}

interface ContentBlockDelta {
  type?: string;
  index?: number;
  delta?: { type?: string; text?: string };
}

/**
 * Async generator yielding text-delta strings from Anthropic's SSE response. Stops cleanly
 * on `message_stop`. Throws on non-OK proxy/upstream status. AbortSignal cancels the underlying
 * fetch; the consumer's `for await` loop ends with an AbortError.
 */
export async function* streamTextDeltas(opts: StreamMessageOpts): AsyncGenerator<string, void, void> {
  // System: a plain string becomes a single cached block so the system prompt is
  // reused across turns. Pre-built block arrays pass through verbatim.
  const systemPayload: SystemBlock[] =
    typeof opts.system === 'string'
      ? [{ type: 'text', text: opts.system, cache_control: { type: 'ephemeral' } }]
      : opts.system;

  let response: Response;
  try {
    response = await fetch(`${PROXY_BASE}/v1/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': opts.apiKey,
      },
      body: JSON.stringify({
        model: opts.model,
        system: systemPayload,
        messages: opts.messages,
        max_tokens: opts.maxTokens ?? 16_384,
        stream: true,
      }),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (err) {
    // Re-throw aborts so the consumer's catch can recognize them.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    if (err instanceof Error && err.name === 'AbortError') throw err;
    // Otherwise: TypeError("Failed to fetch") on the very first request almost
    // always means the local proxy isn't running — re-shape into a specific error
    // so the UI can surface a useful message instead of "Failed to fetch".
    throw new ProxyUnreachableError(PROXY_BASE, err);
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    throw new StreamError(
      `Proxy returned ${response.status}`,
      response.status,
      text,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  const queue: string[] = [];
  const waiterRef: { current: (() => void) | null } = { current: null };
  let upstreamDone = false;
  let upstreamError: unknown = null;

  const onAbort = () => {
    upstreamDone = true;
    upstreamError = new DOMException('Stream aborted by client.', 'AbortError');
    reader.cancel().catch(() => undefined);
    waiterRef.current?.();
  };
  if (opts.signal) {
    if (opts.signal.aborted) {
      onAbort();
    } else {
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }
  }

  const parser = createParser({
    onEvent(event) {
      if (event.event === 'content_block_delta' && event.data) {
        try {
          const parsed = JSON.parse(event.data) as ContentBlockDelta;
          const text = parsed.delta?.text;
          if (parsed.delta?.type === 'text_delta' && typeof text === 'string') {
            queue.push(text);
            waiterRef.current?.();
          }
        } catch {
          // skip malformed event
        }
      } else if (event.event === 'message_stop' || event.event === 'error') {
        upstreamDone = true;
        waiterRef.current?.();
      }
    },
  });

  const pump = (async () => {
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value && opts.onUpstreamBytes) opts.onUpstreamBytes(value.byteLength);
        parser.feed(decoder.decode(value, { stream: true }));
      }
    } catch (err) {
      upstreamError = err;
    } finally {
      upstreamDone = true;
      waiterRef.current?.();
    }
  })();

  try {
    while (!upstreamDone || queue.length > 0) {
      if (queue.length > 0) {
        yield queue.shift() as string;
      } else {
        await new Promise<void>((resolve) => {
          waiterRef.current = resolve;
        });
      }
    }
    if (upstreamError) throw upstreamError;
  } finally {
    opts.signal?.removeEventListener('abort', onAbort);
    try {
      await reader.cancel();
    } catch {
      // ignore
    }
    await pump.catch(() => undefined);
  }
}
